import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import { useApiReady } from "@/hooks/useApi";

interface AccessibleOrg {
  id: number;
  name: string;
  is_mssp?: boolean;
  parent_organization_id?: number | null;
}

interface TenantContextValue {
  selectedTenantSlug: string;
  selectedOrgId: number | undefined;
  accessibleOrgs: AccessibleOrg[];
  setTenant: (slug: string) => void;
  isAll: boolean;
  isMssp: boolean;
  orgNameMap: Map<number, string>;
}

const TenantContext = createContext<TenantContextValue>({
  selectedTenantSlug: "all",
  selectedOrgId: undefined,
  accessibleOrgs: [],
  setTenant: () => {},
  isAll: true,
  isMssp: false,
  orgNameMap: new Map(),
});

function slugify(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isApiReady = useApiReady();

  const { data: accessibleOrgs = [] } = useQuery<AccessibleOrg[]>({
    queryKey: ["organizations", "accessible"],
    queryFn: () => apiClient.get("/api/v1/organization/accessible"),
    staleTime: 60000,
    enabled: isApiReady,
  });

  const selectedTenantSlug =
    searchParams.get("tenant") ?? localStorage.getItem("overviewTenant") ?? "all";

  const selectedOrgId = useMemo(() => {
    if (selectedTenantSlug === "all") return undefined;
    const match = accessibleOrgs.find((o) => slugify(o.name) === selectedTenantSlug);
    return match?.id;
  }, [selectedTenantSlug, accessibleOrgs]);

  const setTenant = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tenant", slug);
    setSearchParams(next, { replace: true });
    localStorage.setItem("overviewTenant", slug);
  };

  const isMssp = useMemo(
    () => accessibleOrgs.some((o) => o.is_mssp === true),
    [accessibleOrgs]
  );

  const orgNameMap = useMemo(
    () => new Map(accessibleOrgs.map((o) => [o.id, o.name])),
    [accessibleOrgs]
  );

  const value = useMemo(
    () => ({
      selectedTenantSlug,
      selectedOrgId,
      accessibleOrgs,
      setTenant,
      isAll: selectedTenantSlug === "all",
      isMssp,
      orgNameMap,
    }),
    [selectedTenantSlug, selectedOrgId, accessibleOrgs, setTenant, isMssp, orgNameMap]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  return useContext(TenantContext);
}
